import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useVerdict } from "@/hooks/use-verdict";
import { VerdictShort } from "./VerdictShort";
import { verdictProseStyle } from "@/lib/verdict-typography";
import {
  logWaitStart,
  logWaitEnd,
  logWaitLongShown,
} from "@/lib/wait-instrumentation";

const VERDICT_LONG_THRESHOLD_S = 25;


/**
 * Bloc rendu en fin de débat, sous la discussion et avant le cadre
 * « Relancer le Conseil ». Affiche le verdict court adapté à la branche.
 *
 * - business / audit : VerdictShort avec indice + phrase + titres recos
 * - tendances        : VerdictShort avec classification + reco (+ phrase + recos)
 * - verif            : CTA vers /verdict/:id (VerdictVerifView intact)
 * - branche inconnue : fallback CTA générique vers /verdict/:id
 */
export function VerdictEndBlock({
  conversationId,
  branch,
}: {
  conversationId: string;
  branch: "business" | "tendances" | "audit" | "verif" | null;
}) {
  const navigate = useNavigate();
  const { data, isLoading, clarityPending, clarityFailed } =
    useVerdict(conversationId);

  // Instrumentation : on mesure la durée d'attente du verdict court
  // (du premier rendu loader jusqu'à la disponibilité de la clarity / d'un
  // fallback). On exclut la branche Vérif (CTA, pas de loader).
  const isVerif = branch === "verif" || data?.branch === "verif";
  const waitingForVerdict =
    !isVerif &&
    (isLoading || (!data && !clarityFailed) || (clarityPending && !data?.verdictClarity));
  const verdictWaitOpenRef = useRef(false);
  useEffect(() => {
    if (waitingForVerdict && !verdictWaitOpenRef.current) {
      verdictWaitOpenRef.current = true;
      logWaitStart("verdict-short", { id: conversationId });
    } else if (verdictWaitOpenRef.current && !waitingForVerdict) {
      verdictWaitOpenRef.current = false;
      logWaitEnd("verdict-short", clarityFailed ? "error" : "ready", {
        id: conversationId,
      });
    }
  }, [waitingForVerdict, clarityFailed, conversationId]);
  useEffect(() => {
    return () => {
      if (verdictWaitOpenRef.current) {
        verdictWaitOpenRef.current = false;
        logWaitEnd("verdict-short", "abandoned", { id: conversationId });
      }
    };
  }, [conversationId]);

  // Branche Vérif : on garde l'écran dédié.
  if (branch === "verif" || data?.branch === "verif") {
    return (
      <div
        style={{
          margin: "24px 0 0",
          padding: "32px 24px",
          background: "#000",
          border: "1px solid #00E5FF55",
          color: "#F5EFE3",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
            fontSize: 10,
            letterSpacing: 4,
            textTransform: "uppercase",
            color: "#00E5FF",
          }}
        >
          — Observations du Conseil —
        </div>
        <p
          style={{
            ...verdictProseStyle,
            margin: 0,
            maxWidth: 540,
          }}
        >
          Le Conseil a consigné ses observations pour confrontation à votre
          conseil (avocat, expert-comptable, banquier…).
        </p>
        <button
          type="button"
          onClick={() =>
            navigate({ to: "/verdict/$id", params: { id: conversationId } })
          }
          style={{
            background: "#00E5FF",
            color: "#000",
            border: "1px solid #00E5FF",
            padding: "14px 32px",
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
            fontSize: 11,
            letterSpacing: 3,
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Voir les observations
        </button>
      </div>
    );
  }

  if (isLoading || (!data && !clarityFailed)) {
    return <LoadingHint label="Le Conseil rédige son verdict…" conversationId={conversationId} />;
  }

  if (clarityPending && !data?.verdictClarity) {
    return <LoadingHint label="Le verdict se précise…" conversationId={conversationId} />;
  }

  const effectiveBranch = data?.branch ?? branch;
  const instrument =
    effectiveBranch === "tendances" && data?.tendancesInstrument
      ? {
          classification: data.tendancesInstrument.classification,
          reco: data.tendancesInstrument.reco,
        }
      : null;

  return (
    <VerdictShort
      conversationId={conversationId}
      clarity={data?.verdictClarity ?? null}
      instrument={instrument}
      fallbackScore={data?.confidenceScore ?? null}
      fallbackPhrase={
        data?.headline || data?.verdictText
          ? (data?.headline || data?.verdictText).slice(0, 240)
          : null
      }
    />
  );
}

const VERDICT_PENDING_MESSAGES = [
  "Le Conseil scelle son verdict…",
  "Pondération des convictions…",
  "Consolidation des recommandations…",
  "Finalisation…",
];

function LoadingHint({ label, conversationId }: { label: string; conversationId: string }) {
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % VERDICT_PENDING_MESSAGES.length),
      4500,
    );
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => {
      clearInterval(t);
      clearInterval(tick);
    };
  }, []);
  const longWait = elapsed >= VERDICT_LONG_THRESHOLD_S;
  const longLoggedRef = useRef(false);
  useEffect(() => {
    if (longWait && !longLoggedRef.current) {
      longLoggedRef.current = true;
      logWaitLongShown("verdict-short", elapsed, { id: conversationId });
    }
  }, [longWait, elapsed, conversationId]);
  const message = VERDICT_PENDING_MESSAGES[idx] ?? label;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        margin: "24px 0 0",
        padding: "40px 24px",
        background: "#000",
        border: "1px solid #00E5FF33",
        color: "#F5EFE3",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 20,
      }}
    >
      <style>{`
        @keyframes hojin-verdict-pulse {
          0%, 100% { transform: scale(1); opacity: 0.55; box-shadow: 0 0 0 0 rgba(168,136,75,0.55); }
          50%      { transform: scale(1.15); opacity: 1;    box-shadow: 0 0 0 14px rgba(168,136,75,0); }
        }
        @keyframes hojin-verdict-msg-fade {
          0%   { opacity: 0; transform: translateY(4px); }
          15%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes hojin-verdict-shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
      `}</style>
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: "#00E5FF",
          animation: "hojin-verdict-pulse 1.6s ease-in-out infinite",
        }}
      />
      <p
        key={idx}
        style={{
          ...verdictProseStyle,
          margin: 0,
          color: "#F5EFE3",
          animation: "hojin-verdict-msg-fade 4.5s ease-in-out both",
          maxWidth: 480,
        }}
      >
        {message}
      </p>
      <div
        aria-hidden
        style={{
          width: 180,
          height: 1,
          background:
            "linear-gradient(90deg, transparent 0%, #00E5FF 20%, #F5EFE3 50%, #00E5FF 80%, transparent 100%)",
          backgroundSize: "200% 100%",
          animation: "hojin-verdict-shimmer 2.4s linear infinite",
        }}
      />
      <span
        style={{
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
          fontSize: 9,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: "#00E5FF99",
        }}
      >
        Cela prend quelques instants
      </span>
    </div>
  );
}


export default VerdictEndBlock;
